import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app, db } from '../../firebase/firebaseConfig';

/* Lightweight hook for the tab-bar badge and the "Message Us" button dot.

   Subscribes ONLY to `supportConversations/{accountUUID}` (the
   conversation doc), not to the full messages subcollection, so it's
   cheap to mount at the AppShell level. Returns the current `unreadByUser`
   count.

   Full message-subscription + send/read flow lives in `useSupportMessages`
   and is only mounted when the user is on the Message Us page.
*/
export function useSupportUnread() {
  const [accountUUID, setAccountUUID] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const auth = getAuth(app);
    const off = auth.onAuthStateChanged(async (user) => {
      if (!user) { setAccountUUID(null); return; }
      try {
        const tokenResult = await user.getIdTokenResult();
        setAccountUUID(tokenResult.claims.accountUUID || null);
      } catch (_) { setAccountUUID(null); }
    });
    return () => off();
  }, []);

  useEffect(() => {
    if (!accountUUID) { setUnreadCount(0); return undefined; }
    const unsub = onSnapshot(
      doc(db, 'supportConversations', accountUUID),
      (snap) => {
        if (snap.exists()) {
          setUnreadCount(snap.data().unreadByUser || 0);
        } else {
          setUnreadCount(0);
        }
      },
      (err) => {
        // Permission-denied is expected when the conversation doesn't
        // exist yet (security rule on read requires the doc to exist).
        if (err?.code !== 'permission-denied') {
          console.warn('useSupportUnread listener error', err?.code || err);
        }
        setUnreadCount(0);
      },
    );
    return () => unsub();
  }, [accountUUID]);

  return unreadCount;
}
